import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { QualityAlertsBell } from "@/components/QualityAlertsBell";
import { MobileOrdersBell } from "@/components/MobileOrdersBell";
import {
  LayoutDashboard,
  Users,
  UserPlus,
  FileText,
  CalendarClock,
  CheckSquare,
  BarChart3,
  Activity,
  ClipboardList,
  Book,
  Settings,
  LogOut,
  UserCog,
  ChevronLeft,
  Bell,
  Search,
  Croissant,
  Package,
  Wheat,
  Send,
  ShoppingCart,
  Landmark,
  FlaskConical,
  UserCircle,
  ChefHat,
  ShieldCheck,
  Zap,
  Store,
  Factory,
  Truck,
  PackageCheck,
  ScrollText,
  Sparkles,
  Award,
  Sparkles as SparklesIcon,
  Smartphone,
  Building2,
  AlertTriangle,
  HandCoins,
  PackageSearch,
  Trash2,
} from "lucide-react";
import { initials, roleLabel } from "@/lib/utils-format";
import { Toaster } from "@/components/ui/sonner";

const NAV = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", module: "dashboard" },
  { to: "/app/store-dashboard", label: "Store Dashboard", icon: Store, testid: "nav-store-dashboard", module: "dashboard", showroomOnly: true },
  { to: "/app/pos", label: "POS Billing", icon: Zap, testid: "nav-pos-billing", module: "pos" },
  { to: "/app/grn", label: "Goods Received", icon: PackageCheck, testid: "nav-grn", module: "grn" },
  { to: "/app/leads", label: "Leads", icon: UserPlus, testid: "nav-leads", module: "leads" },
  { to: "/app/customers", label: "Customers", icon: Users, testid: "nav-customers", module: "customers" },
  { to: "/app/loyalty", label: "Loyalty & Rewards", icon: Award, testid: "nav-loyalty", module: "customers" },
  { to: "/app/shop-admin", label: "B2B Shop Ops", icon: Store, testid: "nav-shop-admin", module: "customers" },
  { to: "/app/b2b-corporate", label: "B2B Corporate", icon: Building2, testid: "nav-b2b-corporate", module: "accounts", roles: ["super_admin", "admin", "chief_accountant", "ho_admin", "accountant"] },
  { to: "/app/waste-damage", label: "Waste & Damage", icon: AlertTriangle, testid: "nav-waste-damage", module: "products", roles: ["super_admin", "admin", "chief_accountant", "ho_admin", "factory_manager", "factory_admin", "production_manager", "qc_manager"] },
  { to: "/app/forecasting", label: "AI Demand Forecast", icon: SparklesIcon, testid: "nav-forecasting", module: "products" },
  { to: "/app/sales", label: "Sales", icon: FileText, testid: "nav-sales", module: "sales" },
  { to: "/app/purchase-orders", label: "Purchase Orders", icon: ShoppingCart, testid: "nav-pos", module: "purchase_orders" },
  { to: "/app/vendor-purchases", label: "Vendor Purchases", icon: HandCoins, testid: "nav-vendor-purchases", module: "vendor_purchases" },
  { to: "/app/ho-reports", label: "Stock Intelligence", icon: PackageSearch, testid: "nav-ho-reports", module: "reports" },
  { to: "/app/disposals", label: "Stock Disposal", icon: Trash2, testid: "nav-disposals", module: "grn", roles: ["super_admin", "admin", "ho_admin", "chief_accountant", "warehouse_manager", "accountant", "showroom_admin", "showroom_sales_manager", "branch_manager"] },
  { to: "/app/products", label: "Products", icon: Package, testid: "nav-products", module: "products" },
  { to: "/app/ingredients", label: "Ingredients", icon: Wheat, testid: "nav-ingredients", module: "ingredients" },
  { to: "/app/recipes", label: "Recipes & Costing", icon: ChefHat, testid: "nav-recipes", module: "recipes" },
  { to: "/app/qc", label: "Lab & QC", icon: FlaskConical, testid: "nav-qc", module: "qc" },
  { to: "/app/manufacturing", label: "Manufacturing", icon: Factory, testid: "nav-manufacturing", module: "recipes" },
  { to: "/app/mfg/factory-dashboard", label: "Factory Dashboard", icon: Factory, testid: "nav-factory-dashboard", module: "recipes" },
  { to: "/app/mfg/executive-dashboard", label: "Executive Dashboard", icon: BarChart3, testid: "nav-executive-dashboard", module: "recipes", executiveOnly: true },
  { to: "/app/mfg/ho-dpr-sync", label: "HO DPR Sync", icon: Factory, testid: "nav-ho-dpr-sync", module: "recipes", executiveOnly: true },
  { to: "/app/bi", label: "Business Intelligence", icon: BarChart3, testid: "nav-bi", module: "accounts", roles: ["super_admin", "admin", "chief_accountant"] },
  { to: "/app/mfg/yield-analysis", label: "Yield Analysis", icon: BarChart3, testid: "nav-yield-analysis", module: "recipes" },
  { to: "/app/deals", label: "Today's Deals", icon: Sparkles, testid: "nav-deals", module: "settings" },
  { to: "/app/mobile-store", label: "Mobile Store", icon: Smartphone, testid: "nav-mobile-store", module: "products" },
  { to: "/app/followups", label: "Follow-ups", icon: CalendarClock, testid: "nav-followups", module: "followups" },
  { to: "/app/tasks", label: "Tasks", icon: CheckSquare, testid: "nav-tasks", module: "tasks" },
  { to: "/app/notifications", label: "Notifications", icon: Send, testid: "nav-notifications", module: "notifications" },
  { to: "/app/markdown-campaigns", label: "Markdown Campaigns", icon: Sparkles, testid: "nav-markdown-campaigns", module: "notifications" },
  { to: "/app/hr", label: "HR & Employees", icon: UserCircle, testid: "nav-hr", module: "hr" },
  { to: "/app/accounts", label: "Accounts", icon: Landmark, testid: "nav-accounts", module: "accounts" },
  { to: "/app/reports", label: "Reports", icon: BarChart3, testid: "nav-reports", module: "reports" },
  { to: "/app/branches", label: "Branches", icon: Store, testid: "nav-branches", module: "branches" },
  { to: "/app/factories", label: "Factories", icon: Factory, testid: "nav-factories", module: "branches", superAdminOnly: true },
  { to: "/app/showrooms", label: "Retail Showrooms", icon: Store, testid: "nav-showrooms", module: "branches", superAdminOnly: true },
  { to: "/app/showroom-monitor", label: "Showroom Monitor", icon: Activity, testid: "nav-showroom-monitor", module: "branches", superAdminOnly: true },
  { to: "/app/ho-planner", label: "HO Production Planner", icon: ClipboardList, testid: "nav-ho-planner", module: "manufacturing", superAdminOnly: true },
  { to: "/app/warehouse-dispatch", label: "Warehouse Dispatch", icon: Truck, testid: "nav-warehouse-dispatch", module: "branches" },
  { to: "/app/masters/recipes-and-skus", label: "Recipes & SKUs Master", icon: Book, testid: "nav-masters-recipes-skus", module: "recipes", superAdminOnly: true },
  { to: "/app/users", label: "Users", icon: UserCog, testid: "nav-users", module: "users" },
  { to: "/app/factory-admins", label: "Factory Admins", icon: ShieldCheck, testid: "nav-factory-admins", module: "users", superAdminOnly: true },
  { to: "/app/roles", label: "Roles & Permissions", icon: ShieldCheck, testid: "nav-roles", module: "roles" },
  { to: "/app/audit-log", label: "Audit Log", icon: ScrollText, testid: "nav-audit-log", module: "audit" },
  { to: "/app/settings", label: "Settings", icon: Settings, testid: "nav-settings", module: "settings" },
];

export default function AppLayout() {
  const { user, logout, hasPerm } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();

  // Multi-tenant isolation: branch-scoped roles (Factory Admin / Factory Manager /
  // Showroom Admin / Showroom Sales Manager) cannot see Super Admin / HO-level tools.
  const role = user?.role_id || user?.role;
  const isFactoryScoped = role === "factory_manager" || role === "factory_admin";
  const isShowroomScoped = role === "showroom_admin" || role === "showroom_sales_manager";
  const isQCScoped = role === "qc_manager" || role === "qc_staff";
  const isBranchScoped = isFactoryScoped || isShowroomScoped || isQCScoped;
  const ADMIN_ONLY_ROUTES = new Set([
    "/app/dashboard",
    "/app/users", "/app/roles", "/app/settings", "/app/audit-log",
    "/app/factories", "/app/factory-admins",
    "/app/showrooms", "/app/showroom-monitor",
    "/app/ho-planner", "/app/masters/recipes-and-skus",
    "/app/branches", "/app/accounts",
    "/app/leads", "/app/loyalty", "/app/shop-admin",
    "/app/deals", "/app/mobile-store",
    "/app/forecasting", "/app/followups", "/app/tasks",
    "/app/markdown-campaigns", "/app/notifications",
    "/app/mfg/executive-dashboard",
  ]);
  const FACTORY_HIDE = new Set([
    "/app/pos", "/app/sales", "/app/customers", "/app/hr",
    "/app/grn", "/app/store-dashboard",
  ]);
  const SHOWROOM_HIDE = new Set([
    "/app/manufacturing", "/app/mfg/factory-dashboard",
    "/app/mfg/yield-analysis", "/app/qc",
    "/app/purchase-orders", "/app/ingredients", "/app/recipes",
    "/app/warehouse-dispatch",
    // Per user spec: showroom staff see ONLY POS, Stock and GRN — hide rest
    "/app/sales", "/app/products", "/app/hr", "/app/customers",
  ]);
  // QC role locking: only Lab & QC is visible; hide everything else including
  // Retail, Sales, Factory operations, Products/Recipes admin, etc.
  const QC_ALLOWED = new Set(["/app/qc"]);

  const visibleNav = NAV
    .filter((n) => hasPerm(n.module, "view"))
    .filter((n) => !n.roles || n.roles.includes(user?.role_id) || n.roles.includes(user?.role))
    .filter((n) => !n.executiveOnly || !user?.branch_id)
    .filter((n) => !n.superAdminOnly || (user?.role === "super_admin" || user?.role_id === "super_admin"))
    .filter((n) => !n.showroomOnly || isShowroomScoped)
    .filter((n) => !isBranchScoped || !ADMIN_ONLY_ROUTES.has(n.to))
    .filter((n) => !isFactoryScoped || !FACTORY_HIDE.has(n.to))
    .filter((n) => !isShowroomScoped || !SHOWROOM_HIDE.has(n.to))
    .filter((n) => !isQCScoped || QC_ALLOWED.has(n.to));

  return (
    <div className="min-h-screen flex bg-[#FDFBF7] text-[#2C1810]">
      {/* Sidebar */}
      <aside
        className={`sidebar-dark ${collapsed ? "w-[76px]" : "w-[248px]"} shrink-0 transition-[width] duration-200 flex flex-col`}
        data-testid="app-sidebar"
      >
        <div className="h-16 flex items-center justify-center px-3 border-b border-[#3a2418] bg-[#4A2B4C]">
          {collapsed ? (
            <div className="w-9 h-9 rounded-md bg-[#4A2B4C] border border-[#F3EAD7]/25 text-[#F3EAD7] flex items-center justify-center font-[Outfit] font-bold text-lg leading-none">
              B
            </div>
          ) : (
            <img
              src="/bakemill-logo-full.webp"
              alt="Bakemill · Sweet moments of life"
              className="h-11 w-auto object-contain"
              data-testid="sidebar-brand-logo"
            />
          )}
        </div>

        <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
          {visibleNav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              className={({ isActive }) =>
                `nav-item flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium ${
                  isActive ? "active" : ""
                }`
              }
            >
              <n.icon className="w-[18px] h-[18px] shrink-0" />
              {!collapsed && <span>{n.label}</span>}
            </NavLink>
          ))}
        </nav>

        <div className="p-2 border-t border-[#3a2418]">
          <button
            onClick={() => setCollapsed((c) => !c)}
            data-testid="sidebar-collapse-btn"
            className="w-full nav-item flex items-center gap-3 px-3 py-2 rounded-md text-sm"
          >
            <ChevronLeft className={`w-4 h-4 transition-transform ${collapsed ? "rotate-180" : ""}`} />
            {!collapsed && <span>Collapse</span>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="h-16 sticky top-0 bg-[#FDFBF7]/95 backdrop-blur border-b border-[#EAE5D9] z-30 flex items-center gap-4 px-6">
          {/* Branded lockup — visible even when sidebar is collapsed */}
          <div className="flex items-center gap-2.5 pr-4 border-r border-[#EAE5D9]" data-testid="topbar-brand">
            <img
              src="/bakemill-logo-full.webp"
              alt="Bakemill"
              className="hidden lg:block h-9 w-auto object-contain rounded"
            />
            <div className="lg:hidden w-9 h-9 rounded-md bg-[#4A2B4C] text-[#F3EAD7] flex items-center justify-center font-[Outfit] font-bold text-[15px] leading-none">
              B
            </div>
            <div className="hidden xl:block leading-tight pl-1">
              <div className="text-[9px] uppercase tracking-[0.22em] text-[#8B7355]">Production Hub</div>
            </div>
          </div>
          <div className="flex-1 max-w-md flex items-center gap-2 bg-white border border-[#EAE5D9] rounded-md px-3 py-2">
            <Search className="w-4 h-4 text-[#8B7355]" />
            <input
              data-testid="topbar-search"
              placeholder="Search…"
              className="flex-1 bg-transparent outline-none text-sm placeholder:text-[#8B7355]"
            />
          </div>
          <button
            data-testid="topbar-search-btn"
            className="hidden"
          />
          <QualityAlertsBell />
          <MobileOrdersBell user={user} />
          <div className="flex items-center gap-3">
            <div className="text-right hidden md:block">
              <div className="text-sm font-semibold leading-tight" data-testid="topbar-user-name">{user?.name}</div>
              <div className="uc-label leading-tight">{roleLabel(user?.role)}</div>
            </div>
            <div className="w-9 h-9 rounded-full bg-[#C05621] text-white flex items-center justify-center text-sm font-semibold">
              {initials(user?.name)}
            </div>
            <button
              onClick={logout}
              data-testid="topbar-logout"
              className="w-9 h-9 rounded-md border border-[#EAE5D9] flex items-center justify-center hover:border-[#C05621] transition-colors"
              title="Logout"
            >
              <LogOut className="w-4 h-4 text-[#5C4033]" />
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto px-6 py-6" data-testid="app-main-content">
          <Outlet />
        </main>
      </div>
      <Toaster position="top-right" richColors />
    </div>
  );
}
